import React from 'react'
import {useTranslation} from 'react-i18next'
import {Form, Input} from 'antd'
import {Currencies, MAX_AMOUNT_LENGTH} from '@/constants'
import {DEFAULT_CURRENCY} from '@/constants/currencies'
import {AMOUNT_PATTERN, START_ZERO} from '@/constants/patterns'
import {addZeroes, getCurrencySymbol, isEmptyOrSpaces, isInteger} from '@/utils'
import {AmountFormFieldProps} from './props'
import {AMOUNT_INPUT} from '@/constants/playwright-ids'

export const AmountFormField: React.FC<AmountFormFieldProps> = (props) => {
    const {t} = useTranslation()
    const {
        form,
        name = 'amount',
        currency = DEFAULT_CURRENCY,
        inputProps,
        rules = [],
        shouldAcceptZero = true,
        isRequired = true,
        amountRequiredMessage = t('AmountFormField.errors.amountRequired'),
        ...rest
    } = props

    return (
        <Form.Item
            name={name}
            label={t('AmountFormField.amount')}
            rules={[
                {required: isRequired, message: amountRequiredMessage},
                {
                    validator: async (field, value) => {
                        if (isEmptyOrSpaces(value)) {
                            return
                        }

                        if (!shouldAcceptZero && parseFloat(value) === 0) {
                            throw new Error(t('AmountFormField.errors.positiveAmount'))
                        }

                        if (parseFloat(value) >= 0) {
                            return
                        }

                        throw new Error(t('AmountFormField.errors.amountInvalid'))
                    }
                },
                ...rules
            ]}
            getValueFromEvent={(e: React.FormEvent<HTMLInputElement>) => {
                const onlyNumberRegexp = AMOUNT_PATTERN
                const value = e.currentTarget.value
                if (!onlyNumberRegexp.test(value) && !isEmptyOrSpaces(value)) {
                    return form.getFieldValue(name)
                } else if (isInteger(value) && START_ZERO.test(value)) {
                    return parseFloat(value)
                } else if (!isInteger(value) && START_ZERO.test(value.split('.')[0])) {
                    return `${parseInt(value.split('.')[0], 10)}.${
                        value.split('.')[1] ? value.split('.')[1] : ''
                    }`
                } else if (value === '.') {
                    return '0.'
                }
                return value
            }}
            {...rest}
        >
            <Input
                placeholder='0.00'
                data-test-id={AMOUNT_INPUT}
                maxLength={MAX_AMOUNT_LENGTH}
                onBlur={() => {
                    if (form.getFieldValue(name)) {
                        form.setFieldsValue({
                            [name.toString()]: addZeroes(Number(form.getFieldValue(name)))
                        })
                        form.validateFields([name])
                    }
                }}
                prefix={getCurrencySymbol(currency)}
                {...inputProps}
            />
        </Form.Item>
    )
}
